import { Box } from "@material-ui/core";
import React, { useMemo, useCallback, useState, useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import { isEqual } from "lodash-es";
import clsx from "clsx";

import {
  ActionLink,
  CloseButtonIcon,
  MobileFloatingButton,
  MobilePopoverCard,
} from "halifax";
import {
  DurationRefinement,
  ExperiencesAvailabilitySort,
  ExperienceTag,
  PriceRefinement,
  StartTimeRefinement,
  TripadvisorRatingRefinement,
} from "redmond";

import "./styles.scss";
import { type MobileAvailabilityFilterProps } from "./container";
import {
  AvailabilitySort,
  DurationFilter,
  PopularFeaturesFilter,
  PriceFilter,
  StartTimeFilter,
  TripadvisorRatingFilter,
} from "../AvailabilityFilters";
import { CLEAR_ALL_FILTERS } from "../../textConstants";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";

export interface IMobileAvailabilityFilterProps
  extends RouteComponentProps,
    MobileAvailabilityFilterProps {
  isFilterModalOpen: boolean;
  onClose: () => void;
  displaySort: boolean;
}

export const MobileAvailabilityFilter = (
  props: IMobileAvailabilityFilterProps
) => {
  const {
    filteredTripAdvisorRating,
    setFilterTripadvisorRating,
    filteredStartTimes,
    setFilterStartTimes,
    filteredDurationTimes,
    setFilterDurationTimes,
    maxExperiencesPrice,
    filteredPrice,
    setFilterPrice,
    experiencesPopularTagsList,
    popularFeatures,
    setFilterPopularFeatures,
    sortOption,
    setSortOption,
    isFilterModalOpen,
    onClose,
    filtersChangedSinceLastSearch,
    displaySort,
    minExperiencesPrice,
    resetFilterAndSort,
    activitiesAvailabilityProperties,
  } = props;

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const [curTripAdvisorRating, setCurTripAdvisorRating] =
    useState<TripadvisorRatingRefinement>(filteredTripAdvisorRating);
  const [curStartTimeRefinement, setCurStartTimeRefinement] =
    useState<StartTimeRefinement[]>(filteredStartTimes);
  const [curDurationRefinement, setCurDurationRefinement] = useState<
    DurationRefinement[]
  >(filteredDurationTimes);
  const [curPriceRefinement, setCurPriceRefinement] =
    useState<PriceRefinement>(filteredPrice);
  const [curExpTags, setCurExpTags] =
    useState<ExperienceTag[]>(popularFeatures);
  const [curSortOption, setCurSortOption] =
    useState<ExperiencesAvailabilitySort>(sortOption);

  const canReset = useMemo(
    () =>
      curTripAdvisorRating !== TripadvisorRatingRefinement.Any ||
      !isEqual(curStartTimeRefinement, []) ||
      !isEqual(curDurationRefinement, []) ||
      curPriceRefinement.maxPriceUSD !== maxExperiencesPrice?.fiat.value ||
      !isEqual(curExpTags, []) ||
      curSortOption !== ExperiencesAvailabilitySort.Recommended,
    [
      curTripAdvisorRating,
      curStartTimeRefinement,
      curDurationRefinement,
      curExpTags,
      curPriceRefinement,
      curSortOption,
    ]
  );

  const handleApply = useCallback(() => {
    setFilterTripadvisorRating(
      curTripAdvisorRating,
      activitiesAvailabilityProperties
    );
    setFilterStartTimes(
      curStartTimeRefinement,
      activitiesAvailabilityProperties
    );
    setFilterDurationTimes(
      curDurationRefinement,
      activitiesAvailabilityProperties
    );
    setFilterPrice(curPriceRefinement, activitiesAvailabilityProperties);
    setFilterPopularFeatures(curExpTags, activitiesAvailabilityProperties);
    setSortOption(curSortOption);
  }, [
    setFilterTripadvisorRating,
    setFilterStartTimes,
    setFilterDurationTimes,
    setFilterPrice,
    setFilterPopularFeatures,
    setSortOption,
    curTripAdvisorRating,
    curStartTimeRefinement,
    curDurationRefinement,
    curPriceRefinement,
    curExpTags,
    curSortOption,
  ]);

  useEffect(() => {
    if (filtersChangedSinceLastSearch) {
      setCurTripAdvisorRating(filteredTripAdvisorRating);
      setCurStartTimeRefinement(filteredStartTimes);
      setCurDurationRefinement(filteredDurationTimes);
      setCurPriceRefinement(filteredPrice);
      setCurExpTags(popularFeatures);
      setCurSortOption(sortOption);
    }
  }, [
    filtersChangedSinceLastSearch,
    filteredTripAdvisorRating,
    filteredStartTimes,
    filteredDurationTimes,
    maxExperiencesPrice,
    filteredPrice,
    experiencesPopularTagsList,
    popularFeatures,
    sortOption,
  ]);

  return (
    <MobilePopoverCard
      open={isFilterModalOpen}
      onClose={onClose}
      fullScreen
      className="experiences-mobile-availability-filter-root"
      bottomButton={
        canReset ? (
          <MobileFloatingButton
            className="apply-filters-floating-button"
            wrapperClassName="apply-filters-floating-button-container"
            onClick={() => {
              handleApply();
              onClose();
            }}
          >
            Apply filters
          </MobileFloatingButton>
        ) : undefined
      }
      topRightButton={
        <ActionLink
          className="filter-close-button"
          content={<CloseButtonIcon />}
          label="Close"
          onClick={onClose}
        />
      }
      topLeftButton={
        canReset ? (
          <ActionLink
            className="filters-reset-button"
            content={CLEAR_ALL_FILTERS}
            onClick={resetFilterAndSort}
          />
        ) : undefined
      }
    >
      <Box
        className={clsx("mobile-availability-filters-container", {
          xmod: isXmodRedesign,
        })}
      >
        <Box>
          {displaySort && (
            <AvailabilitySort
              availabilitySortOption={curSortOption}
              setAvailabilitySortOption={setCurSortOption}
            />
          )}
          <TripadvisorRatingFilter
            filteredTripAdvisorRating={curTripAdvisorRating}
            setFilterTripadvisorRating={setCurTripAdvisorRating}
            activitiesAvailabilityProperties={activitiesAvailabilityProperties}
          />
          <StartTimeFilter
            filteredStartTimes={curStartTimeRefinement}
            setFilterStartTimes={setCurStartTimeRefinement}
            activitiesAvailabilityProperties={activitiesAvailabilityProperties}
          />
          <DurationFilter
            filteredDurationTimes={curDurationRefinement}
            setFilterDurationTimes={setCurDurationRefinement}
            activitiesAvailabilityProperties={activitiesAvailabilityProperties}
          />
          {maxExperiencesPrice && minExperiencesPrice && (
            <PriceFilter
              maxPrice={maxExperiencesPrice}
              filterPrice={curPriceRefinement}
              setFilterPrice={setCurPriceRefinement}
              minPrice={minExperiencesPrice}
              activitiesAvailabilityProperties={
                activitiesAvailabilityProperties
              }
            />
          )}
          <PopularFeaturesFilter
            popularFeaturesList={experiencesPopularTagsList}
            filteredPopularFeatures={curExpTags}
            setFilteredPopularFeatures={setCurExpTags}
            activitiesAvailabilityProperties={activitiesAvailabilityProperties}
          />
        </Box>
      </Box>
    </MobilePopoverCard>
  );
};
